import React, { useCallback, useEffect, useState } from 'react'
import { FaSyncAlt } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import { BASE_URL } from './shared'

const PREVIEW_PATH = '/api/fairclaim-signature-transparency-preview'
const START_PATH = '/api/fairclaim-signature-transparency-start'
const STATUS_PATH = '/api/fairclaim-signature-transparency-status'

type TransparencyRun = {
    id: string
    status: string
    dryRun?: boolean
    total?: number
    processed?: number
    updated?: number
    skipped?: number
    errors?: number
    currentClient?: string | null
    heartbeatAgeSeconds?: number | null
    errorMessage?: string | null
    createdBy?: string | null
    finishedAt?: string | null
    samples?: {
        clientId?: number
        clientRef?: string | null
        source?: string
        before?: string
        after?: string
    }[]
    failures?: { clientId?: number; clientRef?: string | null; message?: string }[]
}

type Preview = {
    totalClients: number
    totalSignatures: number
    activeRun?: TransparencyRun | null
}

const number = (value: number | null | undefined) => (value == null ? '—' : value.toLocaleString('en-GB'))
const errorMessage = (error: any) =>
    error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.'
const isFinished = (run: TransparencyRun | null) => run?.status === 'complete' || run?.status === 'failed'

/**
 * "Signature transparency" — strips the white canvas from every stored
 * FairClaim signature: the base64 on `signature_links` and the PNGs at each
 * claim's S3 signature key are overwritten with the transparent version.
 * Signatures the webhook already delivered transparent are skipped. Dry run
 * does the full transform and reports counts + before/after samples without
 * writing anything.
 */
export const SignatureTransparencyCard = () => {
    const [preview, setPreview] = useState<Preview | null>(null)
    const [previewError, setPreviewError] = useState<string | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [run, setRun] = useState<TransparencyRun | null>(null)
    const [adopted, setAdopted] = useState(false)
    const [starting, setStarting] = useState<'dry' | 'live' | null>(null)
    const [pollError, setPollError] = useState<string | null>(null)
    const [confirmText, setConfirmText] = useState('')

    const loadPreview = useCallback(async () => {
        setPreviewLoading(true)
        setPreviewError(null)
        try {
            const response = await api.post(`${BASE_URL}${PREVIEW_PATH}`, {})
            if (!response.data?.success) throw new Error(response.data?.message || 'Preview failed')
            const data = response.data.preview as Preview
            setPreview(data)
            // Pick up a run started elsewhere (another tab / before a page reload).
            if (data.activeRun?.id) {
                setRun((current) => (current?.id === data.activeRun!.id ? current : data.activeRun!))
                setAdopted(true)
            }
        } catch (error: any) {
            setPreviewError(errorMessage(error))
        } finally {
            setPreviewLoading(false)
        }
    }, [])

    useEffect(() => {
        loadPreview()
    }, [loadPreview])

    const runId = run?.id
    const runStatus = run?.status
    useEffect(() => {
        if (!runId || runStatus === 'complete' || runStatus === 'failed') return
        let stopped = false
        let timer: ReturnType<typeof setTimeout> | null = null

        const poll = async () => {
            try {
                const response = await api.post(`${BASE_URL}${STATUS_PATH}`, { runId })
                if (stopped) return
                if (!response.data?.run) throw new Error(response.data?.message || 'Could not read backfill progress.')
                const next = response.data.run as TransparencyRun
                setRun(next)
                setPollError(null)
                if (isFinished(next)) {
                    loadPreview()
                    return
                }
                timer = setTimeout(poll, 1000)
            } catch (error: any) {
                if (stopped) return
                setPollError(errorMessage(error))
                timer = setTimeout(poll, 3000)
            }
        }

        void poll()
        return () => {
            stopped = true
            if (timer) clearTimeout(timer)
        }
    }, [runId, runStatus, loadPreview])

    const start = async (dryRun: boolean) => {
        setStarting(dryRun ? 'dry' : 'live')
        setPollError(null)
        try {
            const response = await api.post(
                `${BASE_URL}${START_PATH}`,
                dryRun ? { dryRun: true } : { dryRun: false, confirm: 'OVERWRITE_SIGNATURES' },
            )
            if (!response.data?.run?.id) throw new Error(response.data?.message || 'Could not start the backfill.')
            setAdopted(Boolean(response.data.existing))
            setRun(response.data.run as TransparencyRun)
            setConfirmText('')
        } catch (error: any) {
            setPollError(errorMessage(error))
        } finally {
            setStarting(null)
        }
    }

    const running = Boolean(run && !isFinished(run))
    const finished = isFinished(run)
    const stalled = running && (run?.heartbeatAgeSeconds ?? 0) >= 60
    const liveReady = confirmText.trim().toLowerCase() === 'overwrite'
    const progress = run?.total ? Math.min(100, Math.round(((run.processed ?? 0) / run.total) * 100)) : 0

    return (
        <div>
            <p className={styles.footnote} style={{ marginTop: 0 }}>
                Removes the white canvas from every stored FairClaim signature. Each signature is decoded from the
                database (or recovered from S3 when the stored value is not a PNG), made transparent, written back to{' '}
                <code>signature_links</code> in bulk and saved over the PNG at every claim&apos;s S3 signature path.
                Signatures that are already transparent are skipped, so re-running is safe. New API deliveries are made
                transparent on arrival and never need this.
            </p>

            <div className={styles.transparencyStats}>
                <div>
                    <span className={styles.transparencyStatValue}>
                        {previewLoading && !preview ? '…' : number(preview?.totalClients)}
                    </span>
                    <span className={styles.transparencyStatLabel}>Clients with a signature</span>
                </div>
                <div>
                    <span className={styles.transparencyStatValue}>
                        {previewLoading && !preview ? '…' : number(preview?.totalSignatures)}
                    </span>
                    <span className={styles.transparencyStatLabel}>Signature records</span>
                </div>
                <button
                    type="button"
                    className={styles.refreshButton}
                    onClick={loadPreview}
                    disabled={previewLoading}
                    title="Recount the signatures in the FairClaim schema"
                >
                    <FaSyncAlt className={previewLoading ? styles.refreshSpinning : undefined} aria-hidden="true" />
                    {previewLoading ? 'Counting…' : 'Recount'}
                </button>
            </div>
            {previewError && <p className={styles.errorText}>{previewError}</p>}

            {run && (
                <div className={styles.transparencyRun} aria-live="polite">
                    <div className={styles.transparencyRunHeader}>
                        <strong>
                            {run.dryRun ? 'Dry run' : 'Live run'} · {run.status}
                            {stalled ? ' · stalled' : ''}
                        </strong>
                        <span className={styles.footnote} style={{ margin: 0 }}>
                            {run.createdBy ? `Started by ${run.createdBy}` : ''}
                            {running && run.currentClient ? ` · processing ${run.currentClient}` : ''}
                        </span>
                    </div>
                    <div className={styles.transparencyProgressTrack}>
                        <div
                            className={
                                run.status === 'failed'
                                    ? styles.transparencyProgressFailed
                                    : styles.transparencyProgressFill
                            }
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <div className={styles.transparencyCounts}>
                        <span>
                            Processed <strong>{number(run.processed)}</strong> of <strong>{number(run.total)}</strong>{' '}
                            clients
                        </span>
                        <span>
                            {run.dryRun ? 'Would update' : 'Updated'} <strong>{number(run.updated)}</strong>
                        </span>
                        <span>
                            Already transparent <strong>{number(run.skipped)}</strong>
                        </span>
                        <span>
                            Failed <strong>{number(run.errors)}</strong>
                        </span>
                    </div>
                    {adopted && running && (
                        <p className={styles.footnote}>
                            A signature backfill was already in progress, so this shows that run instead of starting a
                            new one.
                        </p>
                    )}
                    {stalled && (
                        <p className={styles.csvPurgeWarning}>
                            This run has not reported progress for {number(run.heartbeatAgeSeconds)} seconds. It will be
                            released automatically, after which you can start a new run.
                        </p>
                    )}
                    {run.errorMessage && <p className={styles.errorText}>{run.errorMessage}</p>}

                    {run.samples && run.samples.length > 0 && (
                        <div className={styles.transparencySamples}>
                            <h5 className={styles.sectionHeading}>
                                First {run.samples.length} signature{run.samples.length === 1 ? '' : 's'}, before and
                                after
                            </h5>
                            <p className={styles.footnote} style={{ marginTop: 0 }}>
                                The checkerboard shows through wherever the canvas is now transparent. Strokes should
                                look identical in both columns.
                            </p>
                            <ul>
                                {run.samples.map((sample) => {
                                    const who = sample.clientRef || `Client ${sample.clientId}`
                                    return (
                                        <li key={`${sample.clientId}-${sample.clientRef ?? ''}`}>
                                            <span className={styles.transparencySampleRef}>
                                                {who}
                                                {sample.source === 's3' ? ' · recovered from S3' : ''}
                                            </span>
                                            <div className={styles.transparencySamplePair}>
                                                <figure>
                                                    <img
                                                        src={`data:image/png;base64,${sample.before}`}
                                                        alt={`Original signature for ${who}`}
                                                    />
                                                    <figcaption>Original</figcaption>
                                                </figure>
                                                <figure className={styles.transparencySampleTransparent}>
                                                    <img
                                                        src={`data:image/png;base64,${sample.after}`}
                                                        alt={`Transparent signature for ${who}`}
                                                    />
                                                    <figcaption>Transparent</figcaption>
                                                </figure>
                                            </div>
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    )}

                    {run.failures && run.failures.length > 0 && (
                        <div className={styles.transparencyFailures}>
                            <h5 className={styles.sectionHeading}>
                                Failures ({number(run.errors)}
                                {run.failures.length < (run.errors ?? 0) ? `, first ${run.failures.length} shown` : ''})
                            </h5>
                            <ul>
                                {run.failures.slice(0, 25).map((failure, index) => (
                                    <li key={`${failure.clientId}-${index}`}>
                                        <span>{failure.clientRef || `Client ${failure.clientId}`}</span>
                                        <span>{failure.message}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {pollError && <p className={styles.errorText}>{pollError}</p>}

            {!running && (
                <div className={styles.csvPurgeConfirm}>
                    <button
                        type="button"
                        className={styles.secondaryButton}
                        disabled={Boolean(starting) || !preview}
                        onClick={() => start(true)}
                        title="Transform every signature and report what would change, without writing to the database or S3"
                    >
                        {starting === 'dry' ? 'Starting…' : finished ? 'Dry run again' : 'Dry run'}
                    </button>
                    <label>
                        Type <strong>overwrite</strong> to enable the live run
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(event) => setConfirmText(event.target.value)}
                            autoComplete="off"
                        />
                    </label>
                    <button
                        type="button"
                        className={styles.dangerButton}
                        disabled={!liveReady || Boolean(starting) || !preview}
                        onClick={() => start(false)}
                        title="Overwrite every white-background signature in the database and S3 with its transparent version"
                    >
                        {starting === 'live' ? 'Starting…' : 'Make all signatures transparent'}
                    </button>
                </div>
            )}
        </div>
    )
}
